'use client'

import cn from 'clsx'
import { type MouseEvent, useCallback, useEffect, useRef } from 'react'
import { Button } from '~/components/button'
import { FormContext, useForm, useFormContext } from './hook'
import type {
    FormProviderProps,
    FormState,
    MessagesProps,
    SubmitButtonProps,
} from './types'

const initialState: FormState = { status: 0, message: '', inputs: {} }

export function Form({
  children,
  className = '',
  action,
  onSuccess = () => {},
  ...props
}: FormProviderProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const { formAction, ...helpers } = useForm({
    action,
    initialState,
  })

  useEffect(() => {
    if (helpers?.formState?.status === 200) {
      onSuccess(helpers?.formState)
    }
  }, [helpers.formState, onSuccess])

  return (
    <FormContext.Provider value={{ ref: formRef, ...helpers }}>
      <form
        ref={formRef}
        className={cn(className)}
        action={formAction}
        {...props}
      >
        {children}
      </form>
    </FormContext.Provider>
  )
}

export const SubmitButton = function SubmitButton({
  children,
  className,
  textStates = {
    error: 'an error occurred. please try again.',
    pending: 'doing the hard work_',
    success: 'success_',
  },
  ...props
}: SubmitButtonProps) {
  const textRef = useRef<HTMLSpanElement>(null)
  const { isFormReady, isFormPending, isFormSuccess, isFormError, formState } =
    useFormContext()
    
  // Determine which success message to show based on the server response
  const getSuccessMessage = () => {
    if (!isFormSuccess) return textStates.success
    
    switch (formState?.message) {
      case 'already_subscribed_':
        return textStates.already_subscribed || textStates.success
      case 'message_received_':
        return textStates.message_received || textStates.success
      default:
        return textStates.success
    }
  }

  const handleClick = useCallback(() => {
    return (e: MouseEvent<HTMLButtonElement>) => {
      if (!isFormReady || isFormPending) {
        e.preventDefault()
      }
    }
  }, [isFormReady, isFormPending])

  return (
    <Button
      type="submit"
      aria-label="submit form"
      aria-disabled={!isFormReady || isFormPending}
      onClick={handleClick}
      className={cn(className, {
        'opacity-50 pointer-events-none': !isFormReady || isFormPending,
      })}
      {...props}
    >
      <span ref={textRef} className="cta grid grid-cols-1">
        <span
          className={cn(
            'col-start-1 row-start-1',
            isFormPending || isFormError || isFormSuccess
              ? 'opacity-0'
              : 'opacity-100'
          )}
        >
          {children}
        </span>
        <span
          className={cn(
            'col-start-1 row-start-1',
            isFormPending ? 'opacity-100' : 'opacity-0'
          )}
        >
          {textStates.pending}
        </span>
        <span
          className={cn(
            'col-start-1 row-start-1',
            isFormError ? 'opacity-100' : 'opacity-0'
          )}
        >
          {textStates.error}
        </span>
        <span
          className={cn(
            'col-start-1 row-start-1',
            isFormSuccess ? 'opacity-100' : 'opacity-0'
          )}
        >
          {getSuccessMessage()}
        </span>
      </span>
    </Button>
  )
}

export const Messages = function Messages({
  className,
  ...props
}: MessagesProps) {
  const { errors = new Map() } = useFormContext()

  // Only render if there are errors with messages
  const errorValues = Array.from(errors.values())
  if (errors.size === 0 || !errorValues.some((error) => error.message)) {
    return null
  }

  return (
    <div className={className} {...props}>
      {Array.from(errors.entries()).map(([id, error]) =>
        error.message ? (
          <p className="p" key={id}>
            {error.message}
          </p>
        ) : null
      )}
    </div>
  )
}
